import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    useTheme,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { SeriesData } from '~/utils/transforms/patientDataTransforms';

const getTypes = (series: SeriesData[]) => {
    const childTypes = series?.map((series) => series.modality);
    return [...new Set(childTypes)].join('/');
};

interface PatientAccordionProps {
    patientKey: string;
    patientData: {
        id: string;
        name: string;
        series: SeriesData[];
    };
    children: ReactNode;
    defaultExpanded?: boolean;
    isSelected?: boolean;
}

export default function PatientAccordion({
    patientKey,
    patientData,
    children,
    defaultExpanded,
    isSelected,
}: PatientAccordionProps) {
    const selectedPatient = useRecoilValue(selectedPatientIdState);
    const isSelectedPatient = isSelected ?? selectedPatient === patientData.id;
    const theme = useTheme();

    const expanded = defaultExpanded ?? isSelectedPatient;

    return (
        <Accordion key={patientKey} defaultExpanded={expanded}>
            <AccordionSummary>
                <Box
                    display="grid"
                    gridTemplateColumns="1fr auto"
                    width="100%"
                    gap={2}
                >
                    <Box
                        width="100%"
                        display={'flex'}
                        flexDirection={'column'}
                        minWidth={0}
                    >
                        <SensitiveInfoTypography
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {patientData.name}
                        </SensitiveInfoTypography>
                        <SensitiveInfoTypography
                            sx={{
                                top: 24,
                                fontSize: 12,
                                color: theme.palette.primary.main,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {patientData.id}
                        </SensitiveInfoTypography>
                    </Box>

                    <Box
                        display="flex"
                        gap={2}
                        justifyContent="center"
                        alignItems="center"
                    >
                        {isSelectedPatient && (
                            <Chip
                                variant="outlined"
                                size="small"
                                color="success"
                                label={<FormattedMessage id="active" />}
                            />
                        )}
                        <Typography fontSize={14} color="primary">
                            {getTypes(patientData.series)}
                        </Typography>
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>{children}</AccordionDetails>
        </Accordion>
    );
}

export { getTypes };
